/**
 * Hell oder dunkel — merken, statt bei jedem Laden zurückzufallen.
 *
 * `07` §7.14 verlangt Hell/Dunkel als Feldanforderung, nicht als Spielerei:
 * Ein Tablet auf der Baustelle steht mittags in der Sonne und abends im
 * Kellergeschoss. Eine Einstellung, die das nächste Laden nicht überlebt, ist
 * für diesen Fall keine.
 *
 * **Zwei Begriffe, und sie auseinanderzuhalten ist der Kern dieser Datei:**
 *
 *     Wahl        was der Benutzer eingestellt hat — hell, dunkel oder
 *                 automatisch. Das ist der Wert, der gemerkt wird.
 *     Erscheinung was daraus gerade folgt — hell oder dunkel. Nur damit
 *                 rechnet das Designsystem.
 *
 * Bis zum 05.09.2026 gab es nur den zweiten Begriff, und das kostete zwei
 * Dinge, die beide erst im Feld auffallen:
 *
 * 1. **Kein Rückweg.** Die Geräteeinstellung galt nur, solange niemand
 *    gewählt hatte. Ein einziger Griff an den Umschalter — auch ein
 *    versehentlicher — legte die Shell für immer fest, und es gab keine
 *    Einstellung, die das rückgängig machte.
 * 2. **Kein Mitziehen.** Die Geräteeinstellung wurde **einmal beim Start**
 *    gelesen. Schaltet das Gerät während der Schicht um — Sonnenuntergang,
 *    Akkusparmodus —, blieb die Shell stehen, bis jemand neu lud. Damit
 *    funktionierte die Automatik ausgerechnet in dem Moment nicht, für den
 *    sie da ist.
 *
 * **Kein Zugriff auf `window` beim Import.** Die Funktionen fragen erst, wenn
 * sie gerufen werden, und fangen den Fall ab, dass es keinen Speicher gibt —
 * im privaten Modus mancher Browser wirft schon der Zugriff auf
 * `localStorage`. Ein Portal, das dort mit einer weißen Seite endet, wäre ein
 * teurer Preis für eine Farbeinstellung.
 */

import { ERSCHEINUNGEN, type Erscheinung } from "@hwc/ui";

const SCHLUESSEL = "hwc.erscheinung";
const ABFRAGE = "(prefers-color-scheme: dark)";

/**
 * Was einstellbar ist. `automatisch` steht vorn, weil es die Vorgabe ist.
 *
 * Die beiden anderen sind absichtlich dieselben Zeichenketten wie die
 * Erscheinungen aus `@hwc/ui`: Wer vor dem 05.09.2026 „hell" gewählt hatte,
 * hat „hell" gespeichert, und das bleibt ohne Umschreiben eine gültige Wahl.
 */
export const WAHLEN = ["automatisch", ...ERSCHEINUNGEN] as const;
export type Wahl = (typeof WAHLEN)[number];

export const VORGABE: Wahl = "automatisch";

function istWahl(wert: unknown): wert is Wahl {
  return typeof wert === "string" && (WAHLEN as readonly string[]).includes(wert);
}

/** Die gespeicherte Wahl, falls es eine gibt und sie gültig ist. */
export function gespeicherteWahl(): Wahl | null {
  try {
    const wert = window.localStorage.getItem(SCHLUESSEL);
    return istWahl(wert) ? wert : null;
  } catch {
    // Privater Modus oder gesperrter Speicher. Kein Grund, die Oberfläche
    // aufzugeben - nur einer, sie nicht zu merken.
    return null;
  }
}

/** Die Wahl, mit der die Shell startet. */
export function anfangsWahl(): Wahl {
  return gespeicherteWahl() ?? VORGABE;
}

/** Was das Gerät meldet. Ohne Angabe gilt hell. */
export function vomGeraet(): Erscheinung {
  try {
    return window.matchMedia(ABFRAGE).matches ? "dunkel" : "hell";
  } catch {
    return "hell";
  }
}

/**
 * Was aus einer Wahl folgt.
 *
 * Reine Funktion mit dem Gerätewert als Argument statt als Abfrage darin: So
 * ist sie ohne Browser prüfbar, und der Aufrufer entscheidet, wann er das
 * Gerät fragt. Das ist der Unterschied zwischen „einmal beim Start" und
 * „sooft es sich ändert".
 */
export function wirksame(wahl: Wahl, geraet: Erscheinung): Erscheinung {
  return wahl === "automatisch" ? geraet : wahl;
}

/** Merkt die Wahl. Schlägt es fehl, bleibt sie für diese Sitzung gültig. */
export function merken(wahl: Wahl): void {
  try {
    window.localStorage.setItem(SCHLUESSEL, wahl);
  } catch {
    // siehe oben
  }
}

/**
 * Sagt Bescheid, wenn das Gerät umschaltet. Gibt den Abbestellweg zurück.
 *
 * Ohne das gälte die Automatik nur bis zum ersten Rendern. Der ältere
 * `addListener` steht als Rückfall daneben: Safari kannte `addEventListener`
 * auf `MediaQueryList` bis 14 nicht, und ein Baustellentablet ist selten das
 * Gerät mit der neuesten Fassung.
 */
export function beiGeraetewechsel(melden: (e: Erscheinung) => void): () => void {
  let liste: MediaQueryList;
  try {
    liste = window.matchMedia(ABFRAGE);
  } catch {
    return () => {};
  }
  const beiAenderung = (e: MediaQueryListEvent) => melden(e.matches ? "dunkel" : "hell");
  if (typeof liste.addEventListener === "function") {
    liste.addEventListener("change", beiAenderung);
    return () => liste.removeEventListener("change", beiAenderung);
  }
  const alt = liste as unknown as {
    addListener?: (f: (e: MediaQueryListEvent) => void) => void;
    removeListener?: (f: (e: MediaQueryListEvent) => void) => void;
  };
  alt.addListener?.(beiAenderung);
  return () => alt.removeListener?.(beiAenderung);
}
